import {
  Badge,
  Box,
  Center,
  Flex,
  Heading,
  IconButton,
  Link,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Progress,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { isEvaluationLocked } from "../qualification/stage";
import { EarlyAccessNote } from "../EarlyAccessNote";
import { hasEarlyBidAccess, isScoringOpen } from "../bidAccess";
import { isBidDeadlinePassed } from "../supplierRows";
import { NotReadyCard } from "../NotReadyCard";
import {
  AwardMethodDto,
  BidV2Status,
  EvaluationModelDto,
  SourcingEventStatus,
  type EvalBidDto,
  type EvalCriterionDto,
  type PriceModelDto,
  type SourcingEventV2Dto,
  useGetEvaluationQuery,
  useSetCriterionRationaleMutation,
  useUpsertPriceOverrideMutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { altKeyLabel, isExactlyAlt } from "../../../../common/platform";
import { alwaysVisibleScrollbar, revealedColumnProps } from "../../../../common/scroll";
import { useProcurementTerms } from "../../../../common/terminology/useProcurementTerms";
import { urls } from "../../../../urls";
import {
  priceModelType,
  usePriceModelDescription,
} from "../../../sourcing-events/common/priceModel";
import { hasTenderStageQualification } from "../../../sourcing-events/buyer/wizard/utils/procedure";
import { isEventAwarded } from "../bidOutcome";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { useAwardCriterionScoreEditor } from "../bid-detail/useBuyerAwardCriterionScoreEditor";
import { isInteractiveClickTarget } from "../../../../common/clickableRow";
import { useRoomForFrozenColumns } from "./useRoomForFrozenColumns";
import { GroupScoreCell, ScoreCell } from "./ScoreCell";
import { ScoreDialog } from "./ScoreDialog";
import { FocusModeDialog } from "./FocusModeDialog";
import { AwardCriterionTypeBadge } from "../../../sourcing-events/bid-readonly/AwardCriterionTypeBadge";
import { evalCriterionOwnLabel } from "./criterionLabel";
import { buildEvaluationRows, isPriceRow } from "./evaluationRows";
import { useStableBidOrder } from "./useStableBidOrder";
import { PriceCell } from "./PriceCell";
import { CriterionRationaleSection } from "./CriterionRationaleSection";
import { EvaluationRanking } from "./EvaluationRanking";
import { MinimumRequirementOutcomeBadge } from "../minimum-requirements/MinimumRequirementOutcomeBadge";
import { PriceSelection } from "./PriceSelection";
import { ProductEvaluationSection } from "./ProductEvaluationSection";
import { formatWeightPercent } from "../../../sourcing-events/buyer/wizard/utils/awardCriteriaStatus";
import { criterionTypeLabelKey } from "../../../sourcing-events/buyer/wizard/utils/publicProcurementOptions";

const DASH = "–";

const BID_COLUMN_REM = 11;
const BID_COLUMN_WIDTH = `${BID_COLUMN_REM}rem`;

const FROZEN_REM = [16, 6, 7] as const;
const FROZEN_TOTAL_REM = FROZEN_REM[0] + FROZEN_REM[1] + FROZEN_REM[2];
const FROZEN_WIDTHS = FROZEN_REM.map((width) => `${width}rem`);
const FROZEN_LEFT = ["0", `${FROZEN_REM[0]}rem`, `${FROZEN_REM[0] + FROZEN_REM[1]}rem`] as const;
const FROZEN_TOTAL = `${FROZEN_TOTAL_REM}rem`;
const MIN_REM_TO_FREEZE = FROZEN_TOTAL_REM + BID_COLUMN_REM;

const isFocusModeShortcut = (event: KeyboardEvent<HTMLElement>) =>
  isExactlyAlt(event) && event.key === "Enter";

const stickyLeft = (offset: string, frozen: boolean) =>
  frozen ? { position: "sticky" as const, left: offset, zIndex: 1 } : {};

const frozenColumn = (index: 0 | 1 | 2, bg: string, frozen: boolean) => ({
  ...stickyLeft(FROZEN_LEFT[index], frozen),
  minW: FROZEN_WIDTHS[index],
  maxW: FROZEN_WIDTHS[index],
  // TableContainer sets white-space: nowrap on everything inside it, which a header too long for its column would otherwise obey.
  whiteSpace: "normal" as const,
  overflow: "hidden" as const,
  // English "EFF. WEIGHT" needs more of the column than the default cell padding leaves it.
  ...(index > 0 ? { px: "3" } : {}),
  bg,
  ...(index === 2 ? FROZEN_EDGE : {}),
});

const NO_BIDS: EvalBidDto[] = [];

const FROZEN_EDGE = { "data-frozen-edge": true } as const;
const priceBand = (inBand: boolean) => (inBand ? { "data-price-band": true } : {});
const SCORABLE_ROW = { "data-scorable": true } as const;
const rejectedCell = (isRejected: boolean) =>
  isRejected ? { "data-rejected": true, bg: "smErrorSoft" } : {};

const BORDERS = {
  "th, td": { borderColor: "smBorderSubtle" },
  "th[data-frozen-edge], td[data-frozen-edge]": {
    borderRightWidth: "1px",
    borderRightColor: "smBorder",
  },
  "tr[data-price-band] > th, tr[data-price-band] > td": {
    borderBottomWidth: "2px",
    borderBottomColor: "smBorder",
  },
} as const;

const ROW_HOVER = {
  "tr[data-scorable]:hover > td:not([data-rejected])": { bg: "smBackgroundSecondary" },
} as const;

const WeightCell = ({
  value,
  emphasis = false,
  column,
  bg,
  frozen,
}: {
  value?: number | null;
  emphasis?: boolean;
  column: 1 | 2;
  bg: string;
  frozen: boolean;
}) => (
  <Td textAlign="right" fontFamily="mono" {...frozenColumn(column, bg, frozen)} whiteSpace="nowrap">
    {value == null ? (
      <Text as="span" color="smMuted">
        {DASH}
      </Text>
    ) : (
      <>
        <Text
          as="span"
          fontWeight={emphasis ? "bold" : "normal"}
          color={emphasis ? "smText" : "smMuted"}
        >
          {formatWeightPercent(value)}
        </Text>
        <Text as="span" color="smMuted">
          {"\u00A0%"}
        </Text>
      </>
    )}
  </Td>
);

const GridKeyboardHint = () => {
  const { t } = useTranslation();
  return (
    <Text fontSize="xs" color="smMuted">
      {t(
        "Arrow keys give the grade and the digits 0–10 set one outright. {{modifier}} with an arrow key moves between the cells, Enter opens the scale, and {{modifier}} with Enter opens focus mode.",
        { modifier: altKeyLabel() },
      )}
    </Text>
  );
};

const awardCriterionDescriptions = (event: SourcingEventV2Dto): Map<string, string> => {
  const descriptions = new Map<string, string>();
  for (const criterion of event.eventData.awardCriteria?.bestPriceQualityRatio?.criteria ?? []) {
    if (criterion.id && criterion.description) descriptions.set(criterion.id, criterion.description);
    for (const sub of criterion.subCriteria ?? []) {
      if (sub.id && sub.description) descriptions.set(sub.id, sub.description);
    }
  }
  return descriptions;
};

const QualificationNotReadyPanel = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const toQualification = new URLSearchParams(searchParams);
  toQualification.set("tab", "qualification");

  return (
    <NotReadyCard
      title={String(t("The evaluation is not ready yet"))}
      description={String(t("The qualification must be completed before the bids can be evaluated."))}
      action={{
        label: String(t("Go to the qualification review")),
        to: `?${toQualification}`,
      }}
    />
  );
};

export const EvaluationTab = ({ event }: { event: SourcingEventV2Dto }) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const eventId = event.id;
  const { selectedRound } = useRoundSelection();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const readOnly = !isScoringOpen(event, selectedOrg);
  const earlyAccess = hasEarlyBidAccess(event, selectedOrg);
  const awarded = isEventAwarded(event.status);
  const { data, isLoading, isError } = useGetEvaluationQuery({
    eventId,
    round: selectedRound?.id,
  });
  const [setRationale] = useSetCriterionRationaleMutation();
  const [upsertPriceOverride] = useUpsertPriceOverrideMutation();
  const scoreEditor = useAwardCriterionScoreEditor(
    eventId,
    data?.bids ?? NO_BIDS,
  );
  const columnBids = useStableBidOrder(data?.bids ?? NO_BIDS);
  const { ref: scrollerRef, hasRoom: frozen } = useRoomForFrozenColumns(MIN_REM_TO_FREEZE);
  const [focused, setFocused] = useState<string | null>(null);
  const [grading, setGrading] = useState<{
    criterionId: string;
    bidId: string;
  } | null>(null);

  if (isEvaluationLocked(event)) {
    return <QualificationNotReadyPanel />;
  }
  if (isLoading) {
    return (
      <Center py="16">
        <Spinner color="smPrimary" />
      </Center>
    );
  }
  if (isError || !data) {
    return (
      <Center py="16">
        <Text color="smError">{t("Could not load the evaluation.")}</Text>
      </Center>
    );
  }
  if (data.bids.length === 0) {
    return (
      <Center py="16">
        <Text color="smMuted">
          {isBidDeadlinePassed(event)
            ? t("No bids delivered - nothing to evaluate")
            : earlyAccess
              ? t("No bids delivered yet.")
              : t(
                  "Evaluation can start once the bid submission deadline has passed",
                )}
        </Text>
      </Center>
    );
  }
  // Every tender was rejected during the qualification: there is nothing left to score, and the
  // competition has to be cancelled or its deadline extended instead.
  if (
    hasTenderStageQualification(
      event.eventContext.type === "Public"
        ? event.eventContext.procedureType
        : undefined,
    ) &&
    data.bids.every((bid) => bid.status === BidV2Status.Rejected)
  ) {
    return (
      <Center py="16">
        <Stack spacing="2" textAlign="center" maxW="lg">
          <Text fontWeight="semibold" color="smText">
            {t("No qualified tenders")}
          </Text>
          <Text fontSize="sm" color="smMuted">
            {t(
              "Every tender was rejected in the qualification. Cancel the competition or extend the deadline to receive new tenders.",
            )}
          </Text>
        </Stack>
      </Center>
    );
  }

  const cellFor = (bid: EvalBidDto, criterion: EvalCriterionDto) =>
    bid.cells.find((c) => c.criterionId === criterion.id);

  const graded = grading && {
    criterion: data.criteria.find((c) => c.id === grading.criterionId),
    bid: data.bids.find((b) => b.bidId === grading.bidId),
  };

  const evaluationRows = buildEvaluationRows(data.criteria);
  const priceRowCount = evaluationRows.filter(isPriceRow).length;

  const walk = evaluationRows.flatMap((row) => (row.kind === "criterion" ? [row.criterion] : []));
  const focusedIndex = walk.findIndex((criterion) => criterion.id === focused);
  const focusedCriterion = focusedIndex >= 0 ? walk[focusedIndex] : undefined;
  const descriptions = awardCriterionDescriptions(event);

  const progressPct =
    data.progress.total > 0
      ? (data.progress.filled / data.progress.total) * 100
      : 0;

  // ADR-033 D3: an event with no award criteria is ranked on price alone — no score cells,
  // progress or rationale. Since SM-952 a private event may have criteria, in which case it
  // falls through to the scored matrix below like any public one.
  if (data.model === EvaluationModelDto.Price) {
    return (
      <Stack spacing="8">
        {event.status === SourcingEventStatus.Published && (
          <PublishedEvaluationNotice event={event} earlyAccess={earlyAccess} />
        )}
        <Box>
          <Heading size="md" mb="1">
            {t("Evaluation")}
          </Heading>
          <Text fontSize="sm" color="smMuted">
            {t("Price evaluation")}
          </Text>
          {readOnly && (
            <Text fontSize="sm" color="smMuted" mt="1">
              {t(
                "Scoring and assessments are available only while the procurement is being evaluated.",
              )}
            </Text>
          )}
        </Box>
        <PriceSelection
          eventId={eventId}
          round={selectedRound?.id}
          bids={data.bids}
          readOnly={readOnly}
          awarded={awarded}
          serverSelectedBidIds={data.selectedBidIds ?? []}
          onPriceOverride={async (bidId, value, comment) => {
            await upsertPriceOverride({
              eventId,
              upsertPriceOverrideRequest: { bidId, value, comment },
            }).unwrap();
          }}
        />
        <ProductEvaluationSection event={event} readOnly={readOnly} />
      </Stack>
    );
  }

  return (
    <Stack spacing="8">
      {event.status === SourcingEventStatus.Published && (
        <PublishedEvaluationNotice event={event} earlyAccess={earlyAccess} />
      )}
      <Box>
        <Heading size="md" mb="1">
          {t("Evaluation")}
        </Heading>
        {data.awardMethod && (
          <Flex align="center" gap="1">
            <Text fontSize="sm" color="smMuted">
              {data.awardMethod === AwardMethodDto.LowestPrice
                ? t("Lowest price")
                : t("Best price-quality ratio")}
            </Text>
            <PriceScoringInfo priceModel={event.eventData.priceModel} />
          </Flex>
        )}
        <Text fontSize="sm" color="smMuted" mt="1">
          {readOnly
            ? t(
                "Scoring and assessments are available only while the procurement is being evaluated.",
              )
            : terms.pointsAreGivenOnEachBid}
        </Text>
      </Box>

      <Flex align="center" gap="3">
        <Progress
          value={progressPct}
          size="sm"
          rounded="full"
          colorScheme={
            data.progress.filled === data.progress.total ? "green" : "teal"
          }
          flex="1"
        />
        <Text
          fontSize="sm"
          color="smMuted"
          whiteSpace="nowrap"
          fontFamily="mono"
        >
          {data.progress.filled}/{data.progress.total} {t("scores")}
        </Text>
      </Flex>

      <Stack spacing="2">
        <Box
          bg="smBackground"
          rounded="xl"
          border="1px solid"
          borderColor="smBorder"
          overflow="hidden"
        >
          <TableContainer
            ref={scrollerRef}
            pb="2"
            scrollPaddingLeft={frozen ? FROZEN_TOTAL : undefined}
            sx={alwaysVisibleScrollbar}
          >
            <Table
              variant="simple"
              sx={{
                borderCollapse: "separate",
                borderSpacing: 0,
                ...BORDERS,
                ...ROW_HOVER,
              }}
            >
              <Thead>
                <Tr {...priceBand(priceRowCount > 0)}>
                  <Th {...frozenColumn(0, "smBackground", frozen)}>
                    {t("Criterion / sub-criterion")}
                  </Th>
                  <Th textAlign="right" {...frozenColumn(1, "smBackground", frozen)}>
                    {t("Weight")}
                  </Th>
                  <Th textAlign="right" {...frozenColumn(2, "smBackground", frozen)}>
                    {t("Eff. weight")}
                  </Th>
                  {columnBids.map((bid) => {
                    const isRejected = bid.status === BidV2Status.Rejected;
                    return (
                      <Th
                        key={bid.bidId}
                        textAlign="center"
                        minW={BID_COLUMN_WIDTH}
                        {...rejectedCell(isRejected)}
                      >
                        <Stack spacing="0.5" align="center">
                          <Link
                            as={RouterLink}
                            to={`${urls.v2.sourcingEvents.bidDetail.go(eventId, bid.bidId)}?tab=awardCriteria`}
                            color="smPrimary"
                          >
                            {bid.supplierName || t("Unknown")}
                          </Link>
                          {isRejected && (
                            <Badge colorScheme="red">{t("Avvist")}</Badge>
                          )}
                          {!isRejected && (
                            <MinimumRequirementOutcomeBadge
                              minimumRequirements={bid.minimumRequirements}
                            />
                          )}
                          {awarded && bid.status === BidV2Status.Awarded && (
                            <Badge colorScheme="green">{t("Selected")}</Badge>
                          )}
                          <PriceCell
                            bid={bid}
                            readOnly={readOnly || isRejected}
                            onSave={async (value, comment) => {
                              await upsertPriceOverride({
                                eventId,
                                upsertPriceOverrideRequest: {
                                  bidId: bid.bidId,
                                  value,
                                  comment,
                                },
                              }).unwrap();
                            }}
                          />
                        </Stack>
                      </Th>
                    );
                  })}
                </Tr>
              </Thead>
              <Tbody>
                {evaluationRows.map((row, index) => {
                  if (row.kind !== "criterion") {
                    return (
                    <Tr
                      key={`group-${row.id}`}
                      bg="smEvalGroupRowBg"
                      {...priceBand(index === priceRowCount - 1)}
                    >
                      <Td {...frozenColumn(0, "smEvalGroupRowBg", frozen)}>
                        <Flex align="center" gap="2">
                          <AwardCriterionTypeBadge type={row.type} />
                          <Text fontWeight="semibold" color="smText">
                            {row.label ?? t(criterionTypeLabelKey(row.type))}
                          </Text>
                        </Flex>
                      </Td>
                      <WeightCell
                        value={row.weightPercent}
                        emphasis
                        column={1}
                        bg="smEvalGroupRowBg"
                        frozen={frozen}
                      />
                      <WeightCell
                        value={row.weightPercent}
                        column={2}
                        bg="smEvalGroupRowBg"
                        frozen={frozen}
                      />
                      {columnBids.map((bid) => {
                        const groupCell = bid.groupCells?.find((cell) => cell.criterionId === row.id);
                        return (
                          <Td
                            key={bid.bidId}
                            textAlign="center"
                            color="smMuted"
                            {...rejectedCell(bid.status === BidV2Status.Rejected)}
                          >
                            {groupCell ? <GroupScoreCell cell={groupCell} /> : DASH}
                          </Td>
                        );
                      })}
                      </Tr>
                    );
                  }
                  const rowBg = row.isSub ? "smBackground" : "smEvalGroupRowBg";
                  return (
                    <Tr
                      key={row.criterion.id}
                      bg={rowBg}
                      {...SCORABLE_ROW}
                      {...priceBand(index === priceRowCount - 1)}
                      cursor="pointer"
                      onClick={(event) => {
                        if (isInteractiveClickTarget(event.target, event.currentTarget)) return;
                        setFocused(row.criterion.id);
                      }}
                    >
                      <Td pl={row.isSub ? "8" : undefined} {...frozenColumn(0, rowBg, frozen)}>
                        <Flex align="center" gap="2">
                          {!row.isSub && (
                            <AwardCriterionTypeBadge
                              type={row.criterion.type}
                            />
                          )}
                          <Text
                            fontWeight={row.isSub ? "normal" : "semibold"}
                            color="smText"
                          >
                            {evalCriterionOwnLabel(row.criterion, String(t(criterionTypeLabelKey(row.criterion.type))))}
                          </Text>
                          {row.criterion.isPrice && (
                            <Badge colorScheme="gray" fontSize="2xs">
                              {t("auto")}
                            </Badge>
                          )}
                        </Flex>
                      </Td>
                      <WeightCell
                        value={row.criterion.weightPercent}
                        emphasis={!row.isSub}
                        column={1}
                        bg={rowBg}
                        frozen={frozen}
                      />
                      <WeightCell
                        value={row.criterion.effectiveWeightPercent}
                        emphasis={row.isSub}
                        column={2}
                        bg={rowBg}
                        frozen={frozen}
                      />
                      {columnBids.map((bid) => {
                        const cell = cellFor(bid, row.criterion);
                        const isRejected = bid.status === BidV2Status.Rejected;
                        const criterionId = row.criterion.id;
                        const gradable =
                          !readOnly &&
                          !isRejected &&
                          cell != null &&
                          !cell.isAutoComputed;
                        return (
                          <Td
                            key={bid.bidId}
                            textAlign="center"
                            {...rejectedCell(isRejected)}
                            {...revealedColumnProps()}
                            onKeyDown={(event) => {
                              if (!isFocusModeShortcut(event)) return;
                              event.preventDefault();
                              setFocused(criterionId);
                            }}
                          >
                            {cell && (
                              <ScoreCell
                                cell={cell}
                                scoring={
                                  gradable
                                    ? {
                                        points: scoreEditor.scoreOf(
                                          bid.bidId,
                                          criterionId,
                                        ).points,
                                        label: `${evalCriterionOwnLabel(row.criterion, String(t(criterionTypeLabelKey(row.criterion.type))))} — ${bid.supplierName || t("Unknown")}`,
                                        onChange: (value) =>
                                          scoreEditor.setPoints(
                                            bid.bidId,
                                            criterionId,
                                            value,
                                          ),
                                        onOpen: () =>
                                          setGrading({
                                            criterionId,
                                            bidId: bid.bidId,
                                          }),
                                      }
                                    : undefined
                                }
                              />
                            )}
                          </Td>
                        );
                      })}
                    </Tr>
                  );
                })}
              </Tbody>
              <Tfoot>
                <Tr>
                  <Td
                    colSpan={3}
                    fontWeight="bold"
                    color="smText"
                    bg="smBackground"
                    {...stickyLeft(FROZEN_LEFT[0], frozen)}
                    {...FROZEN_EDGE}
                  >
                    {t("Total weighted score")}
                  </Td>
                  {columnBids.map((bid) => {
                    const isRejected = bid.status === BidV2Status.Rejected;
                    return (
                      <Td
                        key={bid.bidId}
                        textAlign="center"
                        {...rejectedCell(isRejected)}
                      >
                        <Text
                          fontWeight="bold"
                          fontSize="lg"
                          color={isRejected ? "smErrorStrong" : "smPrimary"}
                          fontFamily="mono"
                        >
                          {bid.weightedTotal.toFixed(2)}
                        </Text>
                      </Td>
                    );
                  })}
                </Tr>
              </Tfoot>
            </Table>
          </TableContainer>
        </Box>

        {!readOnly && <GridKeyboardHint />}
      </Stack>

      <ProductEvaluationSection event={event} readOnly={readOnly} />

      <EvaluationRanking bids={data.bids} awarded={awarded} />

      {event.eventContext.type !== "Private" && (
        <CriterionRationaleSection
          criteria={data.criteria}
          readOnly={readOnly}
          onSave={async (criterionId, text) => {
            await setRationale({
              eventId,
              setRationaleRequest: { criterionId, text },
            }).unwrap();
          }}
        />
      )}

      {focusedCriterion && (
        <FocusModeDialog
          criterion={focusedCriterion}
          descriptionHtml={descriptions.get(focusedCriterion.id) ?? null}
          bids={columnBids}
          cellFor={(bid) => cellFor(bid, focusedCriterion)}
          editor={scoreEditor}
          isReadOnly={readOnly}
          position={{ current: focusedIndex + 1, total: walk.length }}
          onPrevious={() => setFocused(walk[focusedIndex - 1]?.id ?? focusedCriterion.id)}
          onNext={() => setFocused(walk[focusedIndex + 1]?.id ?? focusedCriterion.id)}
          onClose={() => setFocused(null)}
        />
      )}

      {graded?.criterion && graded.bid && (
        <ScoreDialog
          isOpen
          onClose={() => setGrading(null)}
          criterion={graded.criterion}
          criterionName={evalCriterionOwnLabel(graded.criterion, String(t(criterionTypeLabelKey(graded.criterion.type))))}
          supplierName={graded.bid.supplierName || t("Unknown")}
          answer={cellFor(graded.bid, graded.criterion)?.supplierAnswer}
          points={
            scoreEditor.scoreOf(graded.bid.bidId, graded.criterion.id).points
          }
          onChange={(value) => {
            if (graded.bid && graded.criterion) {
              scoreEditor.setPoints(
                graded.bid.bidId,
                graded.criterion.id,
                value,
              );
            }
          }}
        />
      )}
    </Stack>
  );
};

const PriceScoringInfo = ({
  priceModel,
}: {
  priceModel?: PriceModelDto | null;
}) => {
  const { t } = useTranslation();
  const describePriceModel = usePriceModelDescription();
  return (
    <Popover placement="bottom-start" isLazy>
      <PopoverTrigger>
        <IconButton
          aria-label={t("How is the price score calculated?")}
          icon={<FiInfo />}
          size="xs"
          variant="ghost"
          color="smMuted"
        />
      </PopoverTrigger>
      <PopoverContent>
        <PopoverArrow />
        <PopoverBody>
          <Stack spacing="2">
            <Text fontSize="sm" fontWeight="semibold" color="smText">
              {t(priceModelType(priceModel))}
            </Text>
            <Text fontSize="sm" color="smText">
              {describePriceModel(
                priceModelType(priceModel),
                priceModel?.hybrid?.breakpointPercent,
              )}{" "}
              {t("Each criterion's points are then multiplied by its weight.")}
            </Text>
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const PublishedEvaluationNotice = ({
  event,
  earlyAccess,
}: {
  event: SourcingEventV2Dto;
  earlyAccess: boolean;
}) => {
  if (earlyAccess && !isBidDeadlinePassed(event)) {
    return <EarlyAccessNote />;
  }
  return <BeginEvaluationPanel event={event} earlyAccess={earlyAccess} />;
};

const BeginEvaluationPanel = ({ event, earlyAccess }: { event: SourcingEventV2Dto; earlyAccess: boolean }) => {
  const { t } = useTranslation();
  const explanation = isBidDeadlinePassed(event)
    ? earlyAccess
      ? t(
          "The submission deadline has passed. You can already read and score the bids; start the evaluation to move the procurement into evaluation.",
        )
      : t("The submission deadline has passed and the bids are open to you. Start the evaluation to begin scoring.")
    : t("You can begin evaluation once the submission deadline has passed.");

  return (
    <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" px="5" py="4">
      <Heading size="sm" mb="1">
        {t("Begin evaluation")}
      </Heading>
      <Text fontSize="sm" color="smMuted">
        {explanation}
      </Text>
    </Box>
  );
};
